import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Camera, CameraPlus, CaretDown, CaretUp, ChartBar, Gauge, ListNumbers, NotePencil, Plus, Trash, type Icon } from '@phosphor-icons/react';
import { typInfo } from '../../../../lib/zaehler/typen';
import { kurzDatum, standText } from '../../../../lib/zaehler/liste';
import { durchschnittText, monatKurz, verbrauchPille, verbrauchProMonat, verlaufBalken } from '../../../../lib/zaehler/ablesungen';
import type { AblesungZeile, ZaehlerDaten } from '../../../../lib/zaehler/useZaehlerDetail';

/** Glas-Karte mit aufklappbarem Kopf (Handoff Zähler §5.3: Radius 24, Kopf 13/16, Icon 30). */
export function AbschnittKarte({ titel, icon: IconKomp, badge, standardOffen = true, children }: {
  titel: string; icon: Icon; badge?: number; standardOffen?: boolean; children: React.ReactNode;
}) {
  const [offen, setOffen] = useState(standardOffen);
  const Pfeil = offen ? CaretUp : CaretDown;
  return (
    <section className="immo-glas flex flex-col min-w-0" style={{ borderRadius: 24 }} aria-label={titel}>
      <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen} className="flex items-center w-full text-left cursor-pointer"
        style={{ gap: 10, padding: '13px 16px', minHeight: 56, border: 'none', background: 'transparent', color: 'var(--ink)', fontFamily: 'inherit' }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 30, height: 30, borderRadius: 10, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
          <IconKomp weight="fill" size={15} />
        </span>
        <span className="flex-1 min-w-0 truncate" style={{ fontSize: 15, fontWeight: 800 }}>{titel}</span>
        {badge !== undefined && badge > 0 && (
          <span className="immo-zahl flex items-center justify-center shrink-0"
            style={{ minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11, background: 'var(--acc)', color: 'var(--bg)', fontSize: 11, fontWeight: 800 }}>{badge}</span>
        )}
        <Pfeil size={13} weight="bold" color="var(--ink3)" />
      </button>
      {offen && <div className="flex flex-col" style={{ padding: '0 16px 16px', gap: 10 }}>{children}</div>}
    </section>
  );
}

/** Label links, Wert rechts, Trennlinie unten. */
export function DatenZeile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between" style={{ gap: 12, padding: '8px 0', borderBottom: '1px solid var(--line)', fontSize: 14 }}>
      <span style={{ color: 'var(--ink2)' }}>{label}</span>
      <span className="text-right min-w-0" style={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  );
}

function LinkWert({ onClick, vorne, children }: { onClick: () => void; vorne?: string | null; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center cursor-pointer text-right"
      style={{ gap: 5, padding: 0, minHeight: 44, margin: '-10px 0', border: 'none', background: 'transparent', color: 'var(--acc)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}>
      {vorne && <span style={{ color: 'var(--ink3)', fontWeight: 600 }}>{vorne}</span>}
      {children}
      <ArrowUpRight size={12} weight="bold" />
    </button>
  );
}

/** Stammdaten – leere Felder ausgeblendet; Objekt und Mieter führen in ihre Bereiche. */
export function StammdatenKarte({ z }: { z: ZaehlerDaten }) {
  const navigate = useNavigate();
  const mieterName = z.mieter ? [z.mieter.vorname, z.mieter.nachname].filter(Boolean).join(' ') : '';
  return (
    <AbschnittKarte titel="Stammdaten" icon={Gauge}>
      <div className="flex flex-col">
        <DatenZeile label="Nummer"><span className="immo-zahl">{z.zaehler_nummer}</span></DatenZeile>
        <DatenZeile label="Typ">{typInfo(z.typ).label}</DatenZeile>
        {z.einheit && <DatenZeile label="Messeinheit">{z.einheit}</DatenZeile>}
        {z.standort && <DatenZeile label="Standort">{z.standort}</DatenZeile>}
        {z.einheit_bezeichnung && <DatenZeile label="Einheit">{z.einheit_bezeichnung}</DatenZeile>}
        {z.objekt && (
          <DatenZeile label="Objekt">
            <LinkWert vorne={z.objekt.objektnummer} onClick={() => navigate(`/admin/objekte/${z.objekt!.id}`)}>{z.objekt.bezeichnung || '—'}</LinkWert>
          </DatenZeile>
        )}
        {z.mieter && (
          <DatenZeile label="Mieter">
            <LinkWert onClick={() => navigate(`/admin/mieter/${z.mieter!.id}`)}>{mieterName || '—'}</LinkWert>
          </DatenZeile>
        )}
      </div>
    </AbschnittKarte>
  );
}

/** Aktuelle Werte: letzter Stand, Ø Verbrauch/Monat, Anzahl. */
export function WerteKarte({ z, ablesungen }: { z: ZaehlerDaten; ablesungen: readonly AblesungZeile[] }) {
  const letzte = ablesungen[0];
  const avg = verbrauchProMonat(ablesungen);
  return (
    <AbschnittKarte titel="Aktuelle Werte" icon={ChartBar}>
      {letzte ? (
        <>
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 8, borderRadius: 18, padding: '14px 16px', background: 'var(--acc-soft)' }}>
            <span className="flex flex-col">
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--acc)' }}>Letzter Stand</span>
              <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{kurzDatum(letzte.datum)}</span>
            </span>
            <span className="immo-zahl" style={{ fontSize: 24, fontWeight: 800, color: 'var(--acc)', letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>
              {standText(letzte.wert, z.einheit)}
            </span>
          </div>
          <div className="flex flex-col">
            {avg !== null && <DatenZeile label="Ø Verbrauch/Monat"><span className="immo-zahl">{durchschnittText(avg, z.einheit)}</span></DatenZeile>}
            <DatenZeile label="Ablesungen gesamt"><span className="immo-zahl">{ablesungen.length}</span></DatenZeile>
          </div>
        </>
      ) : (
        <span className="text-center" style={{ padding: '12px 0', fontSize: 13, color: 'var(--ink3)' }}>Noch keine Ablesungen</span>
      )}
    </AbschnittKarte>
  );
}

/** Ablesungen, neueste zuerst: Stand, Foto, Datum · Erfasser, Verbrauch; Löschen. */
export function AblesungenKarte({ z, ablesungen, mobil, onNeu, onLoeschen, onFoto, onFotoNeu, fotoLaedt }: {
  z: ZaehlerDaten; ablesungen: readonly AblesungZeile[]; mobil: boolean; onNeu: () => void; onLoeschen: (a: AblesungZeile) => void;
  /** #512: Foto groß ansehen */ onFoto: (a: AblesungZeile) => void;
  /** #512: Foto an eine Ablesung ohne Foto hängen */ onFotoNeu: (a: AblesungZeile, datei: File) => void;
  /** Ablesung, deren Foto gerade hochgeladen wird */ fotoLaedt: string | null;
}) {
  const ziel = mobil ? 44 : 34;
  const dateiFeld = useRef<HTMLInputElement>(null);
  const [fotoZiel, setFotoZiel] = useState<AblesungZeile | null>(null);
  return (
    <AbschnittKarte titel="Ablesungen" icon={ListNumbers} badge={ablesungen.length}>
      <button type="button" onClick={onNeu} className="flex items-center justify-center w-full cursor-pointer"
        style={{ gap: 7, height: mobil ? 44 : 42, borderRadius: 14, border: 'none', background: 'var(--acc-soft)', color: 'var(--acc)', fontSize: 13, fontWeight: 800, fontFamily: 'inherit' }}>
        <Plus size={14} weight="bold" />Neue Ablesung
      </button>
      {ablesungen.length === 0 ? (
        <span className="text-center" style={{ padding: '12px 0', fontSize: 13, color: 'var(--ink3)' }}>Keine Ablesungen vorhanden</span>
      ) : (
        <div className="flex flex-col immo-scroll" style={{ maxHeight: 300, overflowY: 'auto', gap: 2 }}>
          <input ref={dateiFeld} type="file" accept="image/*" className="sr-only" aria-label="Foto für die Ablesung wählen"
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f && fotoZiel) onFotoNeu(fotoZiel, f); setFotoZiel(null); }} />
          {ablesungen.map(a => {
            const pille = verbrauchPille(a.verbrauch);
            return (
              <div key={a.id} className="immo-hover-flaeche flex items-center" style={{ gap: 8, padding: '9px 8px', borderRadius: 12 }}>
                {/* #512: Vorschaubild (Klick = groß) bzw. Foto nachträglich hinzufügen */}
                {a.foto_url ? (
                  <button type="button" onClick={() => onFoto(a)} aria-label={`Foto der Ablesung vom ${kurzDatum(a.datum)} ansehen`} title="Foto ansehen"
                    className="shrink-0 cursor-zoom-in" style={{ width: ziel + 6, height: ziel + 6, padding: 0, border: 'none', borderRadius: 10, overflow: 'hidden', background: 'var(--fill2)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                    <img src={a.foto_url} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </button>
                ) : (
                  <button type="button" disabled={fotoLaedt === a.id} onClick={() => { setFotoZiel(a); dateiFeld.current?.click(); }}
                    aria-label={`Foto zur Ablesung vom ${kurzDatum(a.datum)} hinzufügen`} title="Foto hinzufügen"
                    className="flex items-center justify-center shrink-0 cursor-pointer"
                    style={{ width: ziel + 6, height: ziel + 6, borderRadius: 10, border: '1px dashed var(--line)', background: 'transparent', color: 'var(--ink3)', opacity: fotoLaedt === a.id ? 0.5 : 1 }}>
                    {fotoLaedt === a.id ? <Camera size={15} className="animate-pulse" /> : <CameraPlus size={16} />}
                  </button>
                )}
                <div className="flex flex-col flex-1 min-w-0">
                  <span className="flex items-center" style={{ gap: 6 }}>
                    <b className="immo-zahl truncate" style={{ fontSize: 14, fontWeight: 800 }}>{standText(a.wert, z.einheit)}</b>
                  </span>
                  <span className="immo-zahl truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                    {kurzDatum(a.datum)}{a.erfasser ? ` · ${a.erfasser}` : ''}
                  </span>
                </div>
                {pille && (
                  <span className="immo-zahl inline-flex items-center shrink-0"
                    style={{ height: 24, padding: '0 9px', borderRadius: 12, fontSize: 12, fontWeight: 800, background: pille.negativ ? 'var(--bad-soft)' : 'var(--ok-soft)', color: pille.negativ ? 'var(--bad)' : 'var(--ok)' }}>
                    {pille.text}
                  </span>
                )}
                <button type="button" title="Löschen" aria-label={`Ablesung vom ${kurzDatum(a.datum)} löschen`} onClick={() => onLoeschen(a)}
                  className="flex items-center justify-center shrink-0 cursor-pointer"
                  style={{ width: ziel, height: ziel, borderRadius: ziel / 2, border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
                  <Trash size={15} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </AbschnittKarte>
  );
}

/** Verbrauchsverlauf (standardmäßig zu): Balken je Ablesung, letzte 12. */
export function VerlaufKarte({ z, ablesungen }: { z: ZaehlerDaten; ablesungen: readonly AblesungZeile[] }) {
  const balken = verlaufBalken(ablesungen);
  if (balken.length < 2) return null;
  return (
    <AbschnittKarte titel="Verbrauchsverlauf" icon={ChartBar} standardOffen={false}>
      <div className="flex items-end" style={{ height: 150, gap: 6 }} role="img" aria-label="Verbrauch je Ablesung">
        {balken.map(b => (
          <div key={b.id} className="flex flex-col items-center justify-end flex-1 min-w-0 h-full" style={{ gap: 6 }}>
            <div title={`${kurzDatum(b.datum)}: ${standText(b.verbrauch, z.einheit)}`} className="w-full transition-opacity hover:opacity-100"
              style={{ maxWidth: 34, height: Math.max(4, b.anteil * 118), borderRadius: 8, background: 'var(--acc-fill)', opacity: 0.85 }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink3)' }}>{monatKurz(b.datum)}</span>
          </div>
        ))}
      </div>
    </AbschnittKarte>
  );
}

export function NotizenKarte({ text }: { text: string | null }) {
  if (!text?.trim()) return null;
  return (
    <AbschnittKarte titel="Notizen" icon={NotePencil}>
      <p className="m-0" style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--ink2)', fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>{text}</p>
    </AbschnittKarte>
  );
}
